import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  CircularProgress,
  Chip,
  Box,
  Typography,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { getSharedHistory, removeSharedUser } from '../../../network/apis/file.api';
import { useOrganization } from '../../../context/OrganizationContext';

const SharedHistoryModal = ({ fileId, open, onClose, token, userId }) => {
  const [sharedHistory, setSharedHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { userRole } = useOrganization();

  useEffect(() => {
    if (open && fileId) {
      fetchSharedHistory();
    }
  }, [open, fileId]);

  const fetchSharedHistory = async () => {
    setLoading(true);
    try {
      const history = await getSharedHistory(token, fileId, userId);
      setSharedHistory(history.sharedHistory || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (shareId) => {
    try {
      await removeSharedUser(token, shareId);
      setConfirmDelete(null);
      fetchSharedHistory();
    } catch (error) {
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Permission check: Viewers cannot revoke shared file access
  // All other roles can revoke access to files they shared
  const canRevokeAccess = userRole !== 'viewer';

  return (
    <>
      {/* Confirmation Dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to revoke access for {confirmDelete?.recipientName}?
          </Typography>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button 
              onClick={() => setConfirmDelete(null)}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={() => handleRemoveUser(confirmDelete?.shareId)}
            >
              Revoke Access
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Main Dialog */}
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ 
          bgcolor: 'rgb(1, 12, 107)', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" fontWeight="bold">
            Shared Access History
          </Typography>
          <Button 
            variant="outlined" 
            onClick={onClose}
            sx={{ 
              color: 'white', 
              borderColor: 'white',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.1)',
                borderColor: 'white'
              }
            }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4} minHeight="200px" alignItems="center">
              <CircularProgress />
            </Box>
          ) : sharedHistory.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4} minHeight="200px" alignItems="center" flexDirection="column">
              <Typography variant="subtitle1" color="textSecondary">
                No sharing history found for this file
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: 'background.paper' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Recipient</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
                    {canRevokeAccess && (
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sharedHistory.map((share) => (
                    <React.Fragment key={share.shareId}>
                      <TableRow hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ display: 'flex', alignItems: 'center',borderBottom: 0 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 36, height: 36 }}>
                            {getInitials(share.recipientName)}
                          </Avatar>
                          <Box>
                            <Typography fontWeight="medium">{share.recipientName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" mb={1}>
                            <EmailIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                            <Typography variant="body2">{share.recipientEmail}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={share.isDownloaded ? 'Downloaded' : 'Pending'}
                            color={share.isDownloaded ? 'success' : 'warning'}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              borderRadius: 1,
                              px: 1
                            }}
                          />
                        </TableCell>
                        {canRevokeAccess && (
                          <TableCell align="right">
                            <Tooltip title="Revoke access">
                              <IconButton
                                onClick={() => setConfirmDelete(share)}
                                sx={{
                                  color: 'black',
                                  '&:hover': {
                                    bgcolor: 'rgba(244, 67, 54, 0.1)'
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={canRevokeAccess ? 4 : 3} sx={{ p: 0 }}>
                          <Divider />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SharedHistoryModal;